import { useEffect, useState } from 'react';
import RenderPreviewModal from './RenderPreviewModal';

import { MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS } from '../utils/photorealistic/renderHistoryDisplay';

const actionButtonClass =
  'flex flex-row items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 transition-colors hover:border-primary-300 hover:bg-primary-500 hover:text-primary-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-primary-300';

const RENDER_SETTINGS_METADATA_ROWS = [
  { label: 'Camera', key: 'camera' },
  { label: 'Resolution', key: 'resolution' },
  { label: 'Lighting', key: 'lighting' },
];

function RenderStarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      className="shrink-0"
      aria-hidden
    >
      <path
        d="M9.64036 2.62525C9.57656 2.51143 9.4836 2.41667 9.37102 2.35072C9.25845 2.28477 9.13033 2.25 8.99986 2.25C8.86938 2.25 8.74127 2.28477 8.62869 2.35072C8.51612 2.41667 8.42315 2.51143 8.35936 2.62525C8.20936 2.89825 8.06511 3.17475 7.92661 3.45475C7.49025 4.34046 7.11926 5.25691 6.81661 6.19675C6.71236 6.52 6.40336 6.74425 6.04936 6.754C4.97627 6.78221 3.90668 6.88874 2.84911 7.07275C2.26861 7.17475 2.05036 7.83625 2.46286 8.23975C2.55686 8.33225 2.65186 8.4235 2.74786 8.5135C3.46565 9.19139 4.2246 9.82434 5.02036 10.4087C5.29561 10.6105 5.40886 10.9562 5.30386 11.2727C4.90388 12.475 4.61762 13.7122 4.44886 14.968C4.37386 15.5342 4.97986 15.934 5.50636 15.664C6.55039 15.1287 7.54874 14.5086 8.49136 13.81C8.639 13.7022 8.81707 13.6442 8.99986 13.6442C9.18265 13.6442 9.36071 13.7022 9.50836 13.81C10.4508 14.5089 11.4492 15.129 12.4934 15.664C13.0191 15.934 13.6259 15.5342 13.5509 14.968C13.5219 14.7555 13.4901 14.5442 13.4556 14.3342C13.2823 13.2955 13.0283 12.2719 12.6959 11.2727C12.5909 10.9562 12.7034 10.6105 12.9794 10.4087C13.8822 9.74724 14.7368 9.02241 15.5369 8.23975C15.9494 7.83625 15.7319 7.17475 15.1506 7.07275C14.0932 6.88767 13.0235 6.78113 11.9504 6.754C11.7807 6.7513 11.616 6.69634 11.4787 6.59662C11.3414 6.4969 11.2382 6.35726 11.1831 6.19675C10.7846 4.95975 10.268 3.76324 9.64036 2.62525Z"
        stroke="currentColor"
        strokeWidth="1.125"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RenderActionButton({
  disabled = false,
  onClick,
  renderLoading = false,
  pendingRenderCount = 0,
  className = '',
}) {
  const showCountBadge = renderLoading && pendingRenderCount > 1;
  const label = renderLoading ? 'Rendering…' : 'Render';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative ${actionButtonClass} ${className}`.trim()}
      aria-label={showCountBadge ? `Rendering ${pendingRenderCount} images` : label}
    >
      {showCountBadge ? (
        <span className="absolute right-1.5 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-300 px-1 text-[10px] font-semibold leading-none text-white shadow-sm">
          {pendingRenderCount}
        </span>
      ) : null}
      <RenderStarIcon />
      {label}
    </button>
  );
}

const ExportImagePanel = ({
  showExportImagePanel,
  exportImagePanelCollapsed,
  setExportImagePanelCollapsed,
  canvasImageSrc = '',
  renderSettingsMetadata = {},
  previewSrc = '',
  onGeneratePreview,
  onDiscardPreview,
  onRender,
  onCancel,
  generatePreviewLoading = false,
  previewStatusLabel = '',
  renderLoading = false,
  renderAtCapacity = false,
  pendingRenderCount = 0,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewOutputVisible, setPreviewOutputVisible] = useState(false);

  useEffect(() => {
    if (!previewSrc?.trim()) {
      setPreviewModalOpen(false);
    }
  }, [previewSrc]);

  useEffect(() => {
    if (!showExportImagePanel) {
      setPreviewOutputVisible(false);
    }
  }, [showExportImagePanel]);

  if (!showExportImagePanel) return null;

  const busy = generatePreviewLoading;
  const renderDisabled = generatePreviewLoading || renderAtCapacity;
  const renderCapacityTooltip = renderAtCapacity
    ? `Maximum ${MAX_CONCURRENT_ROOM_IMAGE_RENDER_TASKS} renders queued. Wait for one to finish before starting another.`
    : undefined;
  const hasPreview = Boolean(previewSrc?.trim());
  const hasCanvasImage = Boolean(canvasImageSrc?.trim());
  const showPreviewActions =
    previewOutputVisible && hasPreview && !generatePreviewLoading;

  const handleGeneratePreviewClick = () => {
    setPreviewOutputVisible(true);
    onGeneratePreview?.();
  };

  const handleDiscardPreviewClick = () => {
    setPreviewOutputVisible(false);
    onDiscardPreview?.();
  };

  return (
    <div className="flex shrink-0 min-h-0 items-stretch">
      <button
        type="button"
        onClick={() => setExportImagePanelCollapsed((v) => !v)}
        className="flex h-12 w-6 shrink-0 flex-col items-center justify-center self-center rounded-l-lg border-r border-gray-200/60 bg-white/35 box-border backdrop-blur-xl backdrop-saturate-150 transition-colors"
        title={exportImagePanelCollapsed ? 'Show export image panel' : 'Hide export image panel'}
        aria-expanded={!exportImagePanelCollapsed}
        aria-label={exportImagePanelCollapsed ? 'Show export image panel' : 'Hide export image panel'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 transition-transform duration-300 ease-out ${exportImagePanelCollapsed ? '' : 'rotate-180'}`}
          aria-hidden
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <aside
        className={`shrink-0 min-h-0 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none ${
          exportImagePanelCollapsed ? 'w-0 pointer-events-none' : 'w-[16rem]'
        }`}
        aria-label="Export Image"
      >
        <div className="flex h-full min-h-0 w-[16] min-w-[16rem] flex-col border-l border-gray-200 border-white/50 bg-white/35 px-2 py-4 box-border backdrop-blur-xl backdrop-saturate-150">
          <div className="mb-3 shrink-0 text-center">
            <h3 className="text-[16px] lao-mn text-gray-700">Export Image</h3>
          </div>

          <div className="mb-3 shrink-0">
            <div
              className="relative flex h-[165px] items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-sm"
              role="img"
              aria-label="Current canvas image"
            >
              {hasCanvasImage ? (
                <img
                  src={canvasImageSrc}
                  alt="Current canvas"
                  className="max-h-full max-w-full object-contain rounded-lg overflow-hidden"
                  draggable={false}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                  <svg
                    className="h-8 w-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <circle cx="8.5" cy="10.5" r="1.5" />
                    <path d="M21 16l-5.2-5.2a1.5 1.5 0 0 0-2.1 0L7 17" />
                  </svg>
                </div>
              )}
            </div>
            <p className="mt-1 text-center text-sm font-semibold text-gray-700">Preview</p>
            <dl className="mt-2 shrink-0 space-y-2 px-0.5 text-[11px]">
              {RENDER_SETTINGS_METADATA_ROWS.map(({ label, key }) => (
                <div key={key} className="flex items-start justify-between gap-3">
                  <dt className="shrink-0 font-medium text-gray-600">{label}</dt>
                  <dd className="min-w-0 text-right text-gray-500">
                    {renderSettingsMetadata[key] || '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {previewOutputVisible ? (
            <div className="shrink-0">
              <div
                className="relative flex h-[165px] items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-sm"
                role="region"
                aria-label="Preview image holder"
              >
                {hasPreview && !generatePreviewLoading ? (
                  <button
                    type="button"
                    onClick={() => setPreviewModalOpen(true)}
                    className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-700 shadow-sm transition-colors hover:border-primary-300 hover:bg-white hover:text-primary-300"
                    title="View full preview"
                    aria-label="View full preview"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M15 3h6v6" />
                      <path d="M9 21H3v-6" />
                      <path d="M21 3l-7 7" />
                      <path d="M3 21l7-7" />
                    </svg>
                  </button>
                ) : null}

                {hasPreview ? (
                  <img
                    src={previewSrc}
                    alt="Render preview"
                    className="max-h-full max-w-full object-contain rounded-lg overflow-hidden"
                    draggable={false}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                    <svg
                      className="h-10 w-10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <circle cx="8.5" cy="10.5" r="1.5" />
                      <path d="M21 16l-5.2-5.2a1.5 1.5 0 0 0-2.1 0L7 17" />
                    </svg>
                  </div>
                )}

                {generatePreviewLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[1px]">
                    <svg
                      className="h-8 w-8 animate-spin text-primary-300"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {previewStatusLabel ? (
                      <span className="mt-2 text-xs font-medium capitalize text-primary-300">
                        {previewStatusLabel}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <p className="mt-1 text-center text-sm font-semibold text-gray-700">Preview Output</p>
            </div>
          ) : null}

          {showPreviewActions ? (
            <div className="mt-3 flex shrink-0 flex-row gap-2 hidden">
              <span className="flex-1" title={renderCapacityTooltip}>
                <RenderActionButton
                  disabled={renderDisabled}
                  onClick={() => onRender?.()}
                  renderLoading={renderLoading}
                  pendingRenderCount={pendingRenderCount}
                  className="w-full"
                />
              </span>
              <button
                type="button"
                onClick={() => handleDiscardPreviewClick()}
                disabled={busy}
                className={`${actionButtonClass} flex-1`}
              >
                Cancel
              </button>
            </div>
          ) : null}

          <div className="min-h-0 flex-1" aria-hidden />

          <div className="mt-auto flex shrink-0 flex-col gap-2 pt-4">
            <button
              type="button"
              onClick={handleGeneratePreviewClick}
              disabled={busy}
              className={`${actionButtonClass} w-full`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="shrink-0"
                aria-hidden
              >
                <path
                  d="M15.75 9C15.75 9.88642 15.5754 10.7642 15.2362 11.5831C14.897 12.4021 14.3998 13.1462 13.773 13.773C13.1462 14.3998 12.4021 14.897 11.5831 15.2362C10.7642 15.5754 9.88642 15.75 9 15.75C8.11358 15.75 7.23583 15.5754 6.41689 15.2362C5.59794 14.897 4.85382 14.3998 4.22703 13.773C3.60023 13.1462 3.10303 12.4021 2.76381 11.5831C2.42459 10.7642 2.25 9.88642 2.25 9C2.25 7.20979 2.96116 5.4929 4.22703 4.22703C5.4929 2.96116 7.20979 2.25 9 2.25C10.7902 2.25 12.5071 2.96116 13.773 4.22703C15.0388 5.4929 15.75 7.20979 15.75 9Z"
                  stroke="currentColor"
                  strokeWidth="1.125"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.5385 7.56C10.7535 6.9975 6.651 10.4895 6.7515 15.168M5.25 6.75C5.25 7.14782 5.40804 7.52936 5.68934 7.81066C5.97064 8.09196 6.35218 8.25 6.75 8.25C7.14782 8.25 7.52936 8.09196 7.81066 7.81066C8.09196 7.52936 8.25 7.14782 8.25 6.75C8.25 6.35218 8.09196 5.97064 7.81066 5.68934C7.52936 5.40804 7.14782 5.25 6.75 5.25C6.35218 5.25 5.97064 5.40804 5.68934 5.68934C5.40804 5.97064 5.25 6.35218 5.25 6.75Z"
                  stroke="currentColor"
                  strokeWidth="1.125"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.40039 9.78044C4.42539 9.55994 6.23064 10.5574 7.21839 12.1249"
                  stroke="currentColor"
                  strokeWidth="1.125"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {generatePreviewLoading ? 'Generating…' : 'Generate Preview'}
            </button>

            <div className="flex flex-row gap-2">
              <span className="flex-1" title={renderCapacityTooltip}>
                <RenderActionButton
                  disabled={renderDisabled}
                  onClick={() => onRender?.()}
                  renderLoading={renderLoading}
                  pendingRenderCount={pendingRenderCount}
                  className="w-full"
                />
              </span>
              <button
                type="button"
                onClick={() => onCancel?.()}
                disabled={busy}
                className={`${actionButtonClass} flex-1`}
              >
                <svg
                  className="h-4 w-4 shrink-0"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </aside>

      <RenderPreviewModal
        open={previewModalOpen && hasPreview}
        src={previewSrc}
        onClose={() => setPreviewModalOpen(false)}
      />
    </div>
  );
};

export default ExportImagePanel;
